import { BUILTIN_VISUALIZATION_TYPES, type VisualizationType } from '@aibi/contracts';
import {
  ChartArea,
  ChartBar,
  ChartColumn,
  ChartLine,
  ChartPie,
  ChartScatter,
  Gauge,
  Grid3x3,
  LayoutGrid,
  Map as MapIcon,
  Rows3,
  Shapes,
  Table2,
  Waves,
  type LucideIcon,
} from 'lucide-react';
import { lazy } from 'react';
import { KpiRenderer } from './renderers/KpiRenderer';
import { TableRenderer } from './renderers/TableRenderer';
import { UnsupportedRenderer } from './renderers/UnsupportedRenderer';
import type { RendererDefinition } from './types';

/**
 * Registro de renderers por tipo de visualizacion. El canvas solo llama a `resolve`:
 * agregar un tipo nuevo (incluidos los de plugins) es registrar su definicion, sin
 * modificar el canvas ni las tarjetas.
 *
 * @example
 * registry.register({ type: 'pie', label: 'Circular', icon: ChartPie, component: PieRenderer, available: true });
 */
export class VisualizationRendererRegistry {
  readonly #definitions = new Map<VisualizationType, RendererDefinition>();

  register(definition: RendererDefinition): this {
    this.#definitions.set(definition.type, definition);
    return this;
  }

  has(type: VisualizationType): boolean {
    return this.#definitions.get(type)?.available === true;
  }

  /** Siempre devuelve una definicion: los tipos sin renderer usan el marcador. */
  resolve(type: VisualizationType): RendererDefinition {
    return (
      this.#definitions.get(type) ?? {
        type,
        label: type,
        icon: Shapes,
        component: UnsupportedRenderer,
        available: false,
      }
    );
  }

  list(): readonly RendererDefinition[] {
    return [...this.#definitions.values()];
  }
}

// Los graficos ECharts se cargan bajo demanda para no penalizar la carga inicial.
const BarRenderer = lazy(() =>
  import('./renderers/BarRenderer').then((module) => ({ default: module.BarRenderer })),
);
const ColumnRenderer = lazy(() =>
  import('./renderers/BarRenderer').then((module) => ({ default: module.ColumnRenderer })),
);
const LineRenderer = lazy(() =>
  import('./renderers/LineRenderer').then((module) => ({ default: module.LineRenderer })),
);
const AreaRenderer = lazy(() =>
  import('./renderers/LineRenderer').then((module) => ({ default: module.AreaRenderer })),
);

const ICONS: Readonly<Record<(typeof BUILTIN_VISUALIZATION_TYPES)[number], LucideIcon>> = {
  table: Table2,
  'grouped-table': Rows3,
  pivot: Grid3x3,
  kpi: Gauge,
  bar: ChartBar,
  column: ChartColumn,
  line: ChartLine,
  area: ChartArea,
  pie: ChartPie,
  donut: ChartPie,
  scatter: ChartScatter,
  treemap: LayoutGrid,
  waterfall: Waves,
  map: MapIcon,
  heatmap: Grid3x3,
};

const LABELS: Readonly<Record<(typeof BUILTIN_VISUALIZATION_TYPES)[number], string>> = {
  table: 'Tabla',
  'grouped-table': 'Tabla agrupada',
  pivot: 'Tabla pivote',
  kpi: 'KPI',
  bar: 'Barras',
  column: 'Columnas',
  line: 'Lineas',
  area: 'Area',
  pie: 'Circular',
  donut: 'Anillo',
  scatter: 'Dispersion',
  treemap: 'Mapa de arbol',
  waterfall: 'Cascada',
  map: 'Mapa',
  heatmap: 'Mapa de calor',
};

/** Registro con los renderers integrados; el resto del catalogo queda como "proximamente". */
export function createDefaultRendererRegistry(): VisualizationRendererRegistry {
  const registry = new VisualizationRendererRegistry();
  const implemented = {
    table: TableRenderer,
    'grouped-table': TableRenderer,
    kpi: KpiRenderer,
    bar: BarRenderer,
    column: ColumnRenderer,
    line: LineRenderer,
    area: AreaRenderer,
  } as const;
  for (const type of BUILTIN_VISUALIZATION_TYPES) {
    const component =
      type in implemented ? implemented[type as keyof typeof implemented] : undefined;
    registry.register({
      type,
      label: LABELS[type],
      icon: ICONS[type],
      component: component ?? UnsupportedRenderer,
      available: component !== undefined,
    });
  }
  return registry;
}
